<template>
  <div>
    <button
      v-if="!hideButton"
      @click="toggleCalendar"
      :class="buttonStyle ? buttonStyle : defaultButtonClasses"
      type="button"
    >
      {{ title }}
      <svg
        class="w-2.5 h-2.5 ms-3"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 10 6"
      >
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m1 1 4 4 4-4"
        />
      </svg>
    </button>
    <div v-if="hideButton || isCalendarOpen" class="date-container">
      <div class="p-6">
        <!-- Calendar Header -->
        <div class="flex items-center justify-between mb-6">
          <!-- Prev Button -->
          <button
            @click="previousMonth"
            type="button"
            class="w-10 h-10 flex justify-center items-center text-gray-600 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2"
            :disabled="isPreviousMonthDisabled"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <!-- Month / Year -->
          <div class="flex items-center space-x-3">
            <div class="relative">
              <select
                v-model="selectedMonth"
                class="appearance-none bg-transparent text-lg font-semibold text-gray-900 border-none cursor-pointer pr-8 hover:text-primary transition-colors duration-200 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2"
              >
                <option
                  v-for="(month, index) in availableMonths"
                  :key="index"
                  :value="index"
                >
                  {{ month }}
                </option>
              </select>
              <svg
                class="absolute right-1 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
            <div class="relative">
              <select
                v-model="selectedYear"
                class="appearance-none bg-transparent text-lg font-semibold text-gray-900 border-none cursor-pointer pr-8 hover:text-primary transition-colors duration-200 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2"
              >
                <option v-for="year in years" :key="year" :value="year">
                  {{ year }}
                </option>
              </select>
              <svg
                class="absolute right-1 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>

          <!-- Next Button -->
          <button
            @click="nextMonth"
            type="button"
            class="w-10 h-10 flex justify-center items-center text-gray-600 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        <!-- Calendar Grid -->
        <div class="calendar-grid">
          <!-- Day Headers -->
          <div class="grid grid-cols-7 gap-1 mb-2">
            <div
              v-for="(day, index) in daysOfWeek"
              :key="index"
              class="h-10 flex items-center justify-center text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              {{ day }}
            </div>
          </div>

          <!-- Calendar Days -->
          <div class="grid grid-cols-7 gap-1">
            <!-- Empty cells for previous month -->
            <div
              v-for="n in startDayOfWeek"
              :key="'empty-' + n"
              class="h-10"
            ></div>

            <!-- Days of current month -->
            <button
              v-for="day in daysInMonth"
              :key="day"
              type="button"
              class="h-10 flex items-center justify-center text-sm font-medium rounded-lg transition-all duration-200 relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2"
              :class="{
                'bg-primary text-white shadow-lg scale-105': isSelected(day),
                'text-gray-900 hover:bg-primary/10 hover:text-primary':
                  !isSelected(day) && !isPastDay(day + 1),
                'text-gray-300 cursor-not-allowed': isPastDay(day + 1),
              }"
              @click="selectDate(day)"
              :disabled="isPastDay(day + 1)"
            >
              {{ day }}
              <div
                v-if="isSelected(day)"
                class="absolute inset-0 rounded-lg ring-2 ring-primary/30 ring-opacity-50"
              ></div>
            </button>
          </div>
        </div>

        <!-- Time Selection -->
        <div v-if="showTime" class="mt-8 mb-6">
          <div class="text-center mb-4">
            <h3 class="text-sm font-medium text-gray-700">Select Time</h3>
          </div>
          <div class="flex justify-center items-center space-x-3">
            <div class="relative">
              <input
                v-model="hourInput"
                @input="validateHour"
                @blur="formatHour"
                type="text"
                maxlength="2"
                placeholder="12"
                class="w-16 h-12 text-center text-lg font-medium bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-colors duration-200"
                :class="{
                  'border-red-300 focus:ring-red-200 focus:border-red-400':
                    hourError,
                }"
              />
              <div
                v-if="hourError"
                class="absolute -bottom-5 left-0 text-xs text-red-500"
              >
                0-23
              </div>
            </div>
            <span class="text-2xl font-bold text-gray-400">:</span>
            <div class="relative">
              <input
                v-model="minuteInput"
                @input="validateMinute"
                @blur="formatMinute"
                type="text"
                maxlength="2"
                placeholder="00"
                class="w-16 h-12 text-center text-lg font-medium bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-colors duration-200"
                :class="{
                  'border-red-300 focus:ring-red-200 focus:border-red-400':
                    minuteError,
                }"
              />
              <div
                v-if="minuteError"
                class="absolute -bottom-5 left-0 text-xs text-red-500"
              >
                0-59
              </div>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="mt-6">
          <div class="bg-red-50 border border-red-200 rounded-lg p-3">
            <div class="flex items-center">
              <svg
                class="w-4 h-4 text-red-400 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="text-sm text-red-700">{{ errorMessage }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <label v-if="computedDisplayText" class="flex mt-1 text-sm text-gray-500">{{
      computedDisplayText
    }}</label>
  </div>
</template>

<script>
import "preline/preline";
import { DateTime } from "luxon";

const DEFAULT_DATE_FORMAT = "ccc, dd LLL yyyy";
const DEFAULT_DATE_TIME_FORMAT = "ccc, dd LLL yyyy HH:mm";

const parseDateTime = (value) => {
  if (!value) {
    return DateTime.now();
  }

  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed : DateTime.now();
};

export default {
  emits: [
    "update:modelValue",
    "update:dateDisplay",
    "applyDateTime",
    "isCalendarOpen",
  ],
  props: {
    title: {
      type: String,
      required: false,
    },
    dateDisplay: {
      type: String,
      default: "",
    },
    buttonStyle: {
      type: String,
      required: false,
    },
    selectPastTime: {
      type: Boolean,
      default: false,
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: String,
      default: null,
    },
    displayFormat: {
      type: String,
      default: "",
    },
    showTime: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const initialDateTime = parseDateTime(this.modelValue);
    const initialHour = this.showTime ? initialDateTime.hour : 0;
    const initialMinute = this.showTime ? initialDateTime.minute : 0;

    return {
      isCalendarOpen: this.hideButton,
      isScheduled: false,
      dateAndTime: this.modelValue,
      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      daysOfWeek: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      selectedMonth: initialDateTime.month - 1,
      selectedYear: initialDateTime.year,
      selectedDate: initialDateTime.day,
      selectedHour: initialHour,
      selectedMinute: String(initialMinute).padStart(2, "0"),
      hourInput: String(initialHour).padStart(2, "0"),
      minuteInput: String(initialMinute).padStart(2, "0"),
      hourError: false,
      minuteError: false,
      errorMessage: "",
      defaultButtonClasses:
        "inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-lg transition-colors duration-200 hover:bg-primary-neutral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2",
    };
  },
  watch: {
    modelValue(value) {
      this.syncFromModelValue(value);
    },
  },
  methods: {
    parseDateTime,
    syncFromModelValue(value) {
      const parsed = parseDateTime(value);
      const hour = this.showTime ? parsed.hour : 0;
      const minute = this.showTime ? parsed.minute : 0;

      this.selectedYear = parsed.year;
      this.selectedMonth = parsed.month - 1;
      this.selectedDate = parsed.day;
      this.selectedHour = hour;
      this.selectedMinute = String(minute).padStart(2, "0");
      this.hourInput = String(hour).padStart(2, "0");
      this.minuteInput = String(minute).padStart(2, "0");
      this.dateAndTime = value;
    },
    formatForDisplay(dateTime) {
      const format =
        this.displayFormat ||
        (this.showTime ? DEFAULT_DATE_TIME_FORMAT : DEFAULT_DATE_FORMAT);
      return dateTime.setLocale("en-GB").toFormat(format);
    },
    toggleCalendar() {
      this.isCalendarOpen = !this.isCalendarOpen;
      if (this.isCalendarOpen) {
        const source = this.modelValue || this.dateAndTime;
        this.syncFromModelValue(source);
      }
    },
    isSelected(day) {
      return day === this.selectedDate;
    },
    isPastDay(day) {
      if (this.selectPastTime) {
        return false;
      }
      const today = new Date();
      const selectedDate = new Date(this.selectedYear, this.selectedMonth, day);
      return selectedDate < today;
    },
    selectDate(day) {
      if (!this.isPastDay(day + 1)) {
        this.selectedDate = day;
        // Apply immediately for date-only picker, otherwise wait for time input.
        this.attemptSubmit({ closeOnSuccess: !this.showTime });
      }
    },
    nextMonth() {
      if (this.selectedMonth === 11) {
        this.selectedMonth = 0;
        this.selectedYear++;
      } else {
        this.selectedMonth++;
      }
    },
    previousMonth() {
      if (this.selectedMonth === 0) {
        this.selectedMonth = 11;
        this.selectedYear--;
      } else {
        this.selectedMonth--;
      }
    },
    validateHour() {
      const hour = parseInt(this.hourInput, 10);
      this.hourError = Number.isNaN(hour) || hour < 0 || hour > 23;
      if (!this.hourError) {
        this.selectedHour = hour;
        this.attemptSubmit({ closeOnSuccess: true, skipValidation: true });
      }
    },
    formatHour() {
      if (!this.hourError && this.hourInput) {
        this.hourInput = String(parseInt(this.hourInput, 10)).padStart(2, "0");
      }
    },
    validateMinute() {
      const minute = parseInt(this.minuteInput, 10);
      this.minuteError = Number.isNaN(minute) || minute < 0 || minute > 59;
      if (!this.minuteError) {
        this.selectedMinute = String(minute).padStart(2, "0");
        this.attemptSubmit({ closeOnSuccess: true, skipValidation: true });
      }
    },
    formatMinute() {
      if (!this.minuteError && this.minuteInput) {
        this.minuteInput = String(parseInt(this.minuteInput, 10)).padStart(
          2,
          "0"
        );
      }
    },
    attemptSubmit({ closeOnSuccess = false, skipValidation = false } = {}) {
      if (this.showTime && !skipValidation) {
        this.validateHour();
        this.validateMinute();

        if (this.hourError || this.minuteError) {
          this.errorMessage = "Please enter valid time values.";
          return;
        }
      } else {
        this.hourError = false;
        this.minuteError = false;
      }

      const selectedDateTime = DateTime.fromObject({
        year: this.selectedYear,
        month: this.selectedMonth + 1,
        day: this.selectedDate,
        hour: this.showTime ? parseInt(this.selectedHour, 10) : 0,
        minute: this.showTime ? parseInt(this.selectedMinute, 10) : 0,
      });

      const today = DateTime.now().startOf("day");
      const selectedDate = DateTime.fromObject({
        year: this.selectedYear,
        month: this.selectedMonth + 1,
        day: this.selectedDate,
      }).startOf("day");

      if (
        !this.selectPastTime &&
        today.equals(selectedDate) &&
        selectedDateTime < DateTime.now()
      ) {
        this.errorMessage = "Selected time is in the past.";
        return;
      }

      const sixtyMinutesFromNow = DateTime.now().plus({ minutes: 60 });
      if (!this.selectPastTime && selectedDateTime < sixtyMinutesFromNow) {
        this.errorMessage =
          "Selected time must be scheduled more than 1 hour from now.";
        return;
      }

      this.dateAndTime = selectedDateTime.toISO();
      this.isScheduled = true;
      this.$emit("update:modelValue", this.dateAndTime);
      const displayText = this.formatForDisplay(selectedDateTime);
      this.$emit("update:dateDisplay", displayText);
      this.$emit("applyDateTime", {
        selectedDateTime: this.dateAndTime,
        displayText,
      });
      if (closeOnSuccess) {
        this.$emit("isCalendarOpen", false);
        this.isCalendarOpen = false;
      }
      this.errorMessage = "";
    },
  },
  computed: {
    computedDisplayText() {
      if (this.dateDisplay) {
        return this.dateDisplay;
      }

      if (!this.dateAndTime) {
        return "";
      }

      const parsed = DateTime.fromISO(this.dateAndTime);
      return parsed.isValid ? this.formatForDisplay(parsed) : this.dateAndTime;
    },
    startDayOfWeek() {
      return new Date(this.selectedYear, this.selectedMonth, 1).getDay();
    },
    daysInMonth() {
      const year = this.selectedYear;
      const month = this.selectedMonth;
      return new Date(year, month + 1, 0).getDate();
    },
    years() {
      const currentYear = new Date().getFullYear();
      const startYear = 2024;
      const years = [];

      if (this.selectPastTime) {
        for (let year = startYear; year <= currentYear; year++) {
          years.push(year);
        }
      } else {
        years.push(currentYear, currentYear + 1);
      }
      return years;
    },
    isPreviousMonthDisabled() {
      if (this.selectPastTime) {
        return false;
      }

      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      return (
        this.selectedYear < currentYear ||
        (this.selectedYear === currentYear &&
          this.selectedMonth <= currentMonth)
      );
    },
    availableMonths() {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const months = [...this.months];

      if (!this.selectPastTime && this.selectedYear === currentYear) {
        months.forEach((month, index) => {
          if (index < currentMonth) {
            const daysInMonth = new Date(
              this.selectedYear,
              index + 1,
              0
            ).getDate();
            const pastDays = [];
            for (let day = 1; day <= daysInMonth; day++) {
              const currentDate = new Date(this.selectedYear, index, day);
              if (currentDate < new Date()) {
                pastDays.push(day);
              }
            }
            pastDays.forEach((pastDay) => {
              month = month.replace(` (${pastDay})`, "");
            });
          }
        });
      }
      return months;
    },
  },
};
</script>

<style scoped>
.date-container {
  position: absolute;
  z-index: 50;
  width: 22rem;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border-radius: 1.25rem;
  overflow: hidden;
}

.calendar-grid {
  user-select: none;
}

@media (max-width: 780px) {
  .date-container {
    left: 50%;
    transform: translateX(-50%);
    width: 20rem;
  }
}
</style>
